import React, { useEffect, useRef, useState } from 'react'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import Icon from 'blocks.simple/icon/icon'
import DndDraggable from 'blocks.app/dnd/_draggable/dnd_draggable'
import DndDroppable from 'blocks.app/dnd/_droppable/dnd_droppable'
import { cn } from 'ethcss'
import styles from './catalog__list.jcss'
import item from 'blocks.simple/item/item.jcss'
import translate from 'core/translate'
import helpers from 'core/helpers'
import { activateEditLink, activateEditTable, activateEditText } from 'pages/files/files.state'
import { useDispatch } from 'react-redux'
import { ItemData } from '../../../core/models/Template'
import AnimationWrapper from 'blocks.app/animationWrapper/animationWrapper'
import {
    CatalogCellValueGetter,
    CatalogDraft,
    CatalogField,
    CatalogItem,
    CatalogListItem,
    CatalogListItemData,
    getCatalogValue,
    ICatalogListProps,
    isCatalogItem,
    toCatalogCellNode,
} from '../catalog.types'

export type { ICatalogListProps } from '../catalog.types'

interface IListInnerProps extends ICatalogListProps {
    listItemData: CatalogListItemData
    getCellValue: CatalogCellValueGetter
}

const ListInner = (p_: IListInnerProps) => {
    const [isHovered, setIsHovered] = useState<boolean>(false)
    const [dragData, setDragData] = useState<unknown>(p_.getDragData?.({ item: p_.listItemData.listItem }))
    const dispatch = useDispatch()

    useEffect(() => {
        setDragData(p_.getDragData?.({ item: p_.listItemData.listItem }))
    }, [p_])

    const onDragOver = () => {
        if (!isHovered) {
            setIsHovered(true)
        }
    }
    const onDragLeave = () => {
        if (isHovered) {
            setIsHovered(false)
        }
    }
    const onSelectInfo = (index: number) => {
        if (p_.template.trNoClick) {
            return
        }

        if (p_.onSelectInfo) {
            p_.onSelectInfo(index)
        }
    }
    const onDrop = (data: { dnd_item: string }, e: React.DragEvent) => {
        e.stopPropagation()

        if (data.dnd_item === 'undefined') return

        let droppedItem: CatalogItem

        try {
            const parsedItem: unknown = JSON.parse(data.dnd_item)

            if (!isCatalogItem(parsedItem)) {
                return
            }

            droppedItem = parsedItem
        } catch (error) {
            return
        }

        if (p_.template.multipleMove && p_.multipleSelect && p_.selectedItems.length) {
            const droppedItemInSelected = p_.selectedItems.filter((selectedItem) =>
                p_.template.isEqual(selectedItem, droppedItem)
            )

            if (droppedItemInSelected.length) {
                onMoveMultiple()
                return
            }
        }

        const { listItem } = p_.listItemData
        if (p_.template.isEqual(droppedItem, listItem)) {
            setIsHovered(false)
            return
        }
        const moveMethod = p_.isFolder(droppedItem) ? p_.template.moveFolder : p_.template.moveItem

        moveMethod(droppedItem.id, listItem.id)
        setIsHovered(false)
    }
    const onMoveMultiple = () => {
        const listItem = p_.listItemData.listItem
        const sourceId: number[] = []
        const folderId: number[] = []

        p_.selectedItems.forEach((item: ItemData) => {
            if (!p_.template.isEqual(item, listItem)) {
                p_.isFolder(item) ? folderId.push(item.id) : sourceId.push(item.id)
            }
        })

        const multipleMove = p_.template.multipleMove

        if (!multipleMove) {
            return
        }

        multipleMove(sourceId, folderId, listItem.id).then(() => {
            p_.onResetSelection()
        })
        setIsHovered(false)
    }
    const { listItem, index, folderKey, options } = p_.listItemData

    const active =
        isHovered ||
        (p_.selectedInfo && p_.isEqual && p_.isEqual(p_.selectedInfo, listItem)) ||
        listItem.__view.selected
    const isFolder = p_.isFolder(listItem)
    const customElement: React.ReactNode =
        p_.template.customElementInSelect && p_.template.customElementInSelect(listItem, active)
    const onOpenEditSetting = (listItem: CatalogItem): (() => void) | undefined => {
        if (listItem.type === 'text') {
            return () => dispatch(activateEditText(listItem.id))
        }
        if (listItem.type === 'link') {
            return () => dispatch(activateEditLink(listItem.id))
        }
        if (listItem.type === 'table') {
            return () => dispatch(activateEditTable(listItem.id))
        }
    }
    return (
        <AnimationWrapper>
            <DndDroppable
                tag={'tr'}
                data={dragData}
                dragEnabled={p_.drag}
                dropEnabled={isFolder}
                wrapperComponent={{
                    type: DndDraggable,
                }}
                onDragStart={p_.onDragStart}
                onDragEnd={p_.onDragEnd}
                onDrop={onDrop}
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()

                    if (p_.template) {
                        if (p_.template.dndDblClickDisable) {
                            return
                        }
                    }

                    if (helpers.isDblTouchTap(e)) {
                        if (typeof p_.onSelectFolder === 'function' && p_.isFolder(listItem)) {
                            p_.onSelectFolder(listItem)
                        } else {
                            if (p_.onDoubleClickObject) {
                                p_.onDoubleClickObject(listItem)
                            }
                        }
                        return
                    }

                    onSelectInfo(index)
                }}
                className={cn(
                    p_.template.getItemClassName ? p_.template.getItemClassName(listItem) : '',
                    {
                        [item.tableSelected]: active && !p_.template.hideActiveRowColor,
                    },
                    `test-catalog-item-${listItem.id || ''}`
                )}
            >
                {p_.multipleSelect && !customElement && (
                    <td>
                        <div className={item.tableCell}>
                            <Checkbox
                                onClick={(selected: boolean, e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    p_.onSelect({ selected, item: listItem })
                                }}
                                className={cn(
                                    `test-catalog-item-checkbox-${listItem.id || ''}`,
                                    p_.type === 'displays' ? 'test-device-chbx' : ''
                                )}
                                checked={listItem.__view.selected}
                                mod={active && !p_.template.hideActiveRowColor ? 'circleInv' : 'circle'}
                                disabled={
                                    (p_.disableFolderSelection && p_.isFolder(listItem)) ||
                                    p_.disableMultiSelect(listItem) ||
                                    listItem.type === 'file'
                                }
                            />
                        </div>
                    </td>
                )}
                {p_.treeView && (
                    <td>
                        {Boolean(isFolder) && (
                            <Icon
                                onClick={(e) => {
                                    e.stopPropagation()

                                    if (p_.onFolderOpening) {
                                        p_.onFolderOpening(listItem)
                                    }
                                }}
                                type={listItem.__view.isOpen ? 'arrow_down' : 'arrow_right'}
                            />
                        )}
                    </td>
                )}
                {customElement && (
                    <td>
                        <div className={item.tableCell}>{customElement}</div>
                    </td>
                )}
                {options.map((option, index: number) => (
                    <td
                        key={`catalogListItem_${folderKey}_${listItem.id}_${index}`}
                        onDoubleClick={onOpenEditSetting(listItem)}
                    >
                        {p_.nestedLevel !== undefined && (
                            <div className={item.tableCell}>
                                {p_.getCellValue(listItem, option.id, active, p_.nestedLevel)}
                            </div>
                        )}
                    </td>
                ))}
                {p_.template.onDeleteListItem && (
                    <td>
                        <div className={styles.deleteIcon}>
                            <Icon
                                onClick={function () {
                                    const onDeleteListItem = p_.template.onDeleteListItem

                                    if (onDeleteListItem) {
                                        onDeleteListItem(listItem)
                                    }
                                }}
                                type={'delete'}
                                color={active ? 'blue' : 'darkCloud'}
                            />
                        </div>
                    </td>
                )}
                {p_.onEditListItem && (
                    <td>
                        <div className={styles.deleteIcon}>
                            <Icon
                                onClick={function () {
                                    if (p_.onEditListItem) {
                                        p_.onEditListItem(listItem)
                                    }
                                }}
                                type={'pencil'}
                                color={active ? 'blue' : 'darkCloud'}
                            />
                        </div>
                    </td>
                )}
            </DndDroppable>
        </AnimationWrapper>
    )
}

const CatalogList = (p_: ICatalogListProps) => {
    const tableRef = useRef<HTMLTableElement | null>(null)

    useEffect(() => {
        if (tableRef && tableRef.current) {
            p_.setTableRef?.(tableRef.current)
        }
    }, [tableRef.current])

    const getCellValue: CatalogCellValueGetter = (listItem, id, active, nestedLevel) => {
        const cellRender = p_.template.list

        if (cellRender && cellRender[id]) {
            return cellRender[id](listItem, active, p_, nestedLevel)
        } else if (
            p_.customData &&
            p_.customData.dataList &&
            p_.customData.dataList.length &&
            id !== 'id' &&
            !p_.customData.exceptionParams.some(
                (param) =>
                    !!getCatalogValue(listItem, param.name) && getCatalogValue(listItem, param.name) === param.value
            )
        ) {
            const itemId = getCatalogValue(listItem, 'id')
            const dataItem = p_.customData.dataList.find((item) => item.id === itemId)

            if (dataItem) {
                const defaultField = p_.customData.fields.find((field) => field.id === id)

                if (defaultField && defaultField.useRawData) {
                    return toCatalogCellNode(getCatalogValue(listItem, id))
                }

                const field = dataItem.fields.find((field) => field.id === id)

                if (field) {
                    return field.value
                } else if (p_.customData.alterFieldValue[id]) {
                    return p_.customData.alterFieldValue[id]
                }
            } else if (p_.customData.alterFieldValue[id]) {
                return p_.customData.alterFieldValue[id]
            }
        } else {
            return toCatalogCellNode(getCatalogValue(listItem, id))
        }
    }
    const normalizeItem = (catalogItem: CatalogItem, index: number): CatalogListItem => {
        const normalizedChildren = catalogItem.children
            ? catalogItem.children.map((child, childIndex) => normalizeItem(child, childIndex))
            : undefined

        return {
            ...catalogItem,
            __view: {
                ...catalogItem.__view,
                selected: !!(catalogItem.__view && catalogItem.__view.selected),
                isOpen: !!(catalogItem.__view && catalogItem.__view.isOpen),
                index,
            },
            children: normalizedChildren,
        }
    }

    const renderTree = (items: CatalogItem[], options: CatalogField[]) => {
        const folders = helpers.deepCopy(items).map((catalogItem, index) => normalizeItem(catalogItem, index))

        const nested = folders.filter((catalogItem, _index, catalogItems) => {
            const nestedKey = catalogItem.__view.nestedKey
            const parent = nestedKey
                ? catalogItems.find(
                      (parentItem) =>
                          p_.template.isFolder(parentItem) && parentItem.id === getCatalogValue(catalogItem, nestedKey)
                  )
                : undefined
            if (!parent) return true
            ;(parent.children = parent.children || []).push(catalogItem)
            return false
        })

        const elements: React.ReactNode[] = []

        const loopOptions = (data: CatalogListItem[], nestedLevel: number) => {
            data.forEach((elem) => {
                const isFolder = p_.isFolder(elem) ? 'folder' : 'item'

                elements.push(
                    <ListInner
                        {...p_}
                        getCellValue={getCellValue}
                        key={`catalogListRow_${isFolder}_${elem.id}_${elem.__view.index}`}
                        listItemData={{
                            listItem: elem,
                            index: elem.__view.index,
                            isFolder,
                            options,
                        }}
                        nestedLevel={nestedLevel}
                    />
                )

                if (elem.children && elem.__view.isOpen) {
                    const counter = nestedLevel + 1
                    loopOptions(elem.children, counter)
                }
            })
        }

        loopOptions(nested, 0)
        return elements
    }

    const options: CatalogField[] = p_.customData
        ? p_.customData.fields
        : p_.options
        ? p_.options.filter((option) => !option.hide)
        : []
    let thStyle: {
        [index: string]: string
    } = {}

    if (p_.template.fixedColWidth) {
        thStyle.width = `${90 / options.length}%`
    }

    if (p_.template.templateCellsWidth) {
        if (!Array.isArray(p_.template.templateCellsWidth)) {
            thStyle = p_.template.templateCellsWidth
        }
    }

    const stickyStyles = !p_.disableSticky ? { ...styles.sticky } : ''

    return (
        <table ref={tableRef} className={cn(item.tableWrapper, styles.wrapper, p_.template.tableWrapper)}>
            <thead>
                <tr>
                    {p_.multipleSelect && (
                        <th className={stickyStyles} style={{ verticalAlign: p_.selectAllCheckbox ? 'middle' : 'top' }}>
                            {p_.selectAllCheckbox ? (
                                <Checkbox
                                    wrapperClassName={styles.selectAllWrapper}
                                    className={styles.selectAll}
                                    checked={!!p_.selectedItems.length}
                                    onClick={!p_.selectedItems.length ? p_.onSelectAll : p_.onCancelSelectAll}
                                />
                            ) : (
                                translate('choice')
                            )}
                        </th>
                    )}
                    {p_.treeView && <th className={stickyStyles} />}
                    {options.map((option, index: number) => (
                        <th
                            key={`catalogListHeader_${index}`}
                            style={
                                p_.template.templateCellsWidth
                                    ? {
                                          width: Array.isArray(p_.template.templateCellsWidth)
                                              ? p_.template.templateCellsWidth[index]?.width
                                              : thStyle[option.id],
                                      }
                                    : thStyle
                            }
                            className={stickyStyles}
                        >
                            {option.name}
                        </th>
                    ))}
                    {p_.template.onDeleteListItem && <th style={{ width: '32px' }} />}
                </tr>
            </thead>
            <tbody>
                {p_.fakeLine || p_.allowEmptyList ? (
                    <tr key={`catalogListRow_${'fake'}`}>
                        {p_.multipleSelect && (
                            <td>
                                <div className={item.tableCell}>
                                    <Checkbox
                                        checked={false}
                                        onClick={(_selected, e) => e.stopPropagation()}
                                        mod={'circle'}
                                        disabled={true}
                                    />
                                </div>
                            </td>
                        )}
                        {options.map((option, index) => (
                            <td key={`catalogListItem_fake_${index}`}>
                                <div className={item.tableCell}>
                                    {p_.fakeLine ? getCellValue(p_.fakeLine, option.id, true) : null}
                                </div>
                            </td>
                        ))}
                        {p_.template.onDeleteListItem && <td></td>}
                    </tr>
                ) : null}
                {renderTree(p_.list, options)}
            </tbody>
        </table>
    )
}

CatalogList.defaultProps = {
    multipleSelect: true,
}

export default CatalogList
